import React, { useEffect, useState, useRef } from "react";
import LeadDbBulkHistoryService from "../../components/Services/LeadDbBulkHistoryService";
import { userId, userRole, userUid } from "../../utils/storage";
import { useNavigate } from "react-router-dom";
import { getBasePathByUserRole } from "../../utils/roleRoutes";
import { showErrorToast, showInfoToast, showSuccessToast } from "../../components/Constant/Notification";
import { formatDate } from "../../components/Constant/Constant";

const useLeadBulkHistory = () => {
  const [leadBulkHistory, setLeadBulkHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading , setDeleteLoading] = useState(false);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [prevPageToken, setPrevPageToken] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [quality, setQuality] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [visible, setVisible] = useState(false);
  const [pageTokens, setPageTokens] = useState([null]);
  const [clearClickCount, setClearClickCount] = useState(0);
  const navigate = useNavigate();

  const historyCache = useRef({});

  const formatDateOnly = (dateStr) => {
    return dateStr ? new Date(dateStr).toISOString().split("T")[0] : null;
  };
  const getLeadBulkHistory = async ({
    pageToken: incomingPageToken = undefined,
    pageSize: pageSizeParam = pageSize,
    direction,
    page = 1,
    forceRefresh = false, // new flag to bypass cache
  }) => {
    // Decide which token to use for this fetch:
    const tokenToUse = incomingPageToken !== undefined ? incomingPageToken : (pageTokens[page - 1] ?? null);
    const size = pageSizeParam;
  
    const cacheKey = `${size}-${tokenToUse || "initial"}-${formatDateOnly(startDate)}-${formatDateOnly(endDate)}-${quality?.label || "no-quality"}`;
  
    // return cached response unless forced
    if (!forceRefresh && historyCache.current[cacheKey]) {
      const cached = historyCache.current[cacheKey];
      setLeadBulkHistory(cached.bulkUploadHistories);
      setNextPageToken(cached.next);
      setPrevPageToken(cached.prev);
      setCurrentPage(page);
      setHasMore(!!cached.next);
      console.log("[cache] returned for", cacheKey);
      return;
    }
  
    setLoading(true);
    try {
      const body = {
        limit: size,
        // keep consistent return type for empty strings
        startDate: startDate ? formatDate(startDate) : "",
        endDate: endDate ? formatDate(endDate) : "",
        userId: userRole === "BOD" ? userUid : userRole === "Lead Uploader" ? userUid : null,
        quality: quality?.label || null,
        next: tokenToUse,
        direction,
      };
  
      const response = await LeadDbBulkHistoryService.getLeadDbBulkHistory(body);
      const data = response?.data || {};
      const nextToken = data?.next || null;
      const prevToken = data?.prev || null;
  
      setIsFilterOpen(false);
      setLeadBulkHistory(data?.bulkUploadHistories || []);
      setNextPageToken(nextToken);
      setPrevPageToken(prevToken);
      setCurrentPage(page);
      setHasMore(!!nextToken);
  
      // Cache the response
      historyCache.current[cacheKey] = {
        bulkUploadHistories: data?.bulkUploadHistories || [],
        next: nextToken,
        prev: prevToken,
      };
  
      // Maintain pageTokens array consistently:
      // pageTokens[0] -> token to fetch page 1 (null)
      // pageTokens[1] -> token to fetch page 2
      // pageTokens[page - 1] should be token used to fetch current page
      setPageTokens((prev) => {
        const newTokens = [...prev];
        // ensure array has at least page slots
        while (newTokens.length < page) newTokens.push(null);
        // token used to fetch current page:
        newTokens[page - 1] = tokenToUse ?? null;
        // token that will fetch next page (page+1)
        newTokens[page] = nextToken ?? null;
        return newTokens;
      });
  
      console.log("[fetch] page:", page, "usedToken:", tokenToUse, "nextToken:", nextToken, "pageTokens:", pageTokens);
    } catch (error) {
      console.error("Error fetching lead bulk history:", error);
      // you can show toast here if desired
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLeadBulkHistory({ pageSize, pageToken: null, page: 1 });
  }, []);

  const handlePageSizeChange = async (size) => {
    setPageSize(size);
    setCurrentPage(1);
    setPageTokens([null]);
    historyCache.current = {};
    await getLeadBulkHistory({ pageSize: size, pageToken: null, page: 1 });
  };

  const handlePageChange = async (token, direction) => {
    console.log("Page change requested:", { direction, currentPage });
  
    if (direction === "next" && hasMore) {
      const newPage = currentPage + 1;
      // when going next we use nextPageToken (provided by last fetch)
      await getLeadBulkHistory({
        pageToken: nextPageToken,
        pageSize,
        direction: "next",
        page: newPage,
        forceRefresh: true, // optional: ensure fresh page
      });
    } else if (direction === "previous" && currentPage > 1) {
      const newPage = currentPage - 1;
      // token for that previous page should be at index (newPage - 1)
      const prevToken = pageTokens[newPage - 1] ?? null;
  
      const cacheKey = `${pageSize}-${prevToken || "initial"}-${formatDateOnly(startDate)}-${formatDateOnly(endDate)}-${quality?.label || "no-quality"}`;
  
      if (historyCache.current[cacheKey]) {
        const cached = historyCache.current[cacheKey];
        setLeadBulkHistory(cached.bulkUploadHistories);
        setNextPageToken(cached.next);
        setPrevPageToken(cached.prev);
        setCurrentPage(newPage);
        setHasMore(!!cached.next);
      } else {
        await getLeadBulkHistory({
          pageToken: prevToken,
          pageSize,
          direction: "previous",
          page: newPage,
        });
      }
    }
  };  

  const handleToggelFilter = () => {
    setIsFilterOpen(true);
  };

  const handleApplyFilter = ({ quality, startDate, endDate }) => {

 
        if (startDate && !endDate) {
          showErrorToast("End Date is required when Start Date is selected.");
          return;
        }
    setQuality(quality);
    setStartDate(startDate);
    setEndDate(endDate);
    setCurrentPage(1);
    setPageTokens([null]);
    historyCache.current = {};
    getLeadBulkHistory({ pageSize, pageToken: null, page: 1 });
  };

  const clearFilters = () => {
    setQuality("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
    setPageTokens([null]);
    historyCache.current = {};
    setClearClickCount((prev) => {
      const newCount = prev + 1;
      if (newCount === 2) {
        getLeadBulkHistory({ pageSize, pageToken: null, page: 1 });
        return 0;
      }
      return newCount;
    });
  };

  const path = getBasePathByUserRole();
  const handleOnViewLeads = (batchId) => {
    navigate(`${path}/lead-bulk-upload-data`, {
      state: { batchId },
    });
  };

  const handleOnRevertLeads = async (batchId) => {
    try {
      const body = {
        batchId: batchId,
        deletedBy: userUid,
      };

      await LeadDbBulkHistoryService.deleteBulkUpload(body);
      showSuccessToast("Lead has been deleted. Once deleted, the details will be sent to the email.");
      setVisible(false);
      historyCache.current = {};

        const tokenForCurrentPage = pageTokens[currentPage - 1] ?? null;
      await getLeadBulkHistory({
      pageToken: tokenForCurrentPage,
      pageSize,
      page: currentPage,
      forceRefresh: true,
    });

    setVisible(false);
     
    } catch (error) {
      console.error("Error reverting leads:", error);
      showErrorToast(error.response?.data || error.message || "An error occurred while reverting leads.");
    }
  };

  return {
    leadBulkHistory,
    getLeadBulkHistory,
    handlePageSizeChange,
    handlePageChange,
    pageSize,
    currentPage,
    nextPageToken,
    prevPageToken,
    hasMore,
    loading,
    setIsFilterOpen,
    isFilterOpen,
    quality,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    handleApplyFilter,
    handleToggelFilter,
    clearFilters,
    setQuality,
    formData,
    setFormData,
    handleOnViewLeads,
    visible,
    setVisible,
    handleOnRevertLeads
  };
};

export default useLeadBulkHistory;